import { FC, useState, useEffect } from 'react';

import { Form, Typography, Badge, Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FormInstance } from 'antd/lib/form';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import SearchInput from '@/components/RSearchInput';
import Table from '@/components/RTable';
import { Plant } from '@/types/plant';
import { Scenario } from '@/types/scenario';
import { SimulationResult } from '@/types/simulations';

interface SelectSimulationProps {
  form: FormInstance;
}

const SelectSimulation: FC<SelectSimulationProps> = ({ form }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [pageSize, setPageSize] = useState<number>(10);
  const [offset, setOffset] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: completedSimulationsData,
    isLoading: completedSimulationsDataIsLoading,
  } = useSWR(
    `/simulation/?limit=${pageSize}&offset=${
      (offset - 1) * pageSize
    }&status=SUCCESS${debouncedSearchTerm ? `&name=${encodeURIComponent(debouncedSearchTerm)}` : ''}`,
    {
      revalidateOnFocus: false,
    }
  );

  const columns: ColumnsType<SimulationResult> = [
    {
      width: 120,
      title: t('components.simReport.selectSimulation.columns.name'),
      dataIndex: 'name',
      ellipsis: true,
      sorter: (a, b) => {
        const nameA = a.name ?? '';
        const nameB = b.name ?? '';

        return nameA.localeCompare(nameB);
      },
      key: 'name',
    },
    {
      width: 75,
      title: t('components.simReport.selectSimulation.columns.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      sorter: (a, b) => {
        const nameA = a.description ?? '';
        const nameB = b.description ?? '';

        return nameA.localeCompare(nameB);
      },
      render: (description: string) => (
        <Typography.Text type="secondary">{description || '-'}</Typography.Text>
      ),
    },
    {
      title: t('components.simReport.selectSimulation.columns.plants'),
      dataIndex: 'plants',
      key: 'plants',
      ellipsis: true,
      width: 100,
      sorter: (a, b) => {
        const nameA = a.plants[0]?.name ?? '';
        const nameB = b.plants[0]?.name ?? '';

        return nameA.localeCompare(nameB);
      },
      render: (dataIndex) => {
        if (!dataIndex || dataIndex.length === 0) {
          return <Typography.Text type="secondary">-</Typography.Text>;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {dataIndex.map((plant: Plant) => (
              <Typography.Text key={plant.id}>{plant.name}</Typography.Text>
            ))}
          </div>
        );
      },
    },
    {
      width: 100,
      title: t('components.simReport.selectSimulation.columns.scenarios'),
      dataIndex: 'scenario',
      key: 'scenario ',
      ellipsis: true,
      sorter: (a, b) => {
        const nameA = a.scenario?.name ?? '';
        const nameB = b.scenario?.name ?? '';

        return nameA.localeCompare(nameB);
      },
      render: (dataIndex: Scenario) => (
        <Typography.Text>{dataIndex?.name || '-'}</Typography.Text>
      ),
    },
    {
      title: t('components.simReport.selectSimulation.columns.storageSystems'),
      dataIndex: 'storage',
      key: 'storage',
      ellipsis: true,
      width: 100,
      sorter: (a, b) => {
        const nameA = a.storage?.name ?? '';
        const nameB = b.storage?.name ?? '';

        return nameA.localeCompare(nameB);
      },
      render: (dataIndex) => (
        <Typography.Text>{dataIndex?.name || '-'}</Typography.Text>
      ),
    },
    {
      width: 75,
      title: t('components.simReport.selectSimulation.columns.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => {
        const nameA = a.createdAt ?? '';
        const nameB = b.createdAt ?? '';

        return nameA.localeCompare(nameB);
      },
      render: (createdAt: string) => (
        <Typography.Text type="secondary">
          {moment(createdAt).format('DD.MM.YYYY HH:mm')}
        </Typography.Text>
      ),
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const currentPageSimulations = completedSimulationsData?.results || [];
    const selectedSimulations = form.getFieldValue('selectedSimulations') || [];

    const previousSelectedSims = selectedSimulations.filter(
      (sim: SimulationResult) =>
        !currentPageSimulations.some(
          (currentSim: SimulationResult) => currentSim.id === sim.id
        )
    );

    const newSelectedSims = currentPageSimulations.filter(
      (sim: SimulationResult) => newSelectedRowKeys.includes(sim.id)
    );
    const allSelectedSims = [...previousSelectedSims, ...newSelectedSims];

    if (allSelectedSims.length > 20) {
      return;
    }

    form.setFieldValue('selectedSimulations', allSelectedSims);
  };

  const rowSelection = {
    selectedRowKeys: (form.getFieldValue('selectedSimulations') || []).map(
      (sim: SimulationResult) => sim.id
    ),
    onChange: onSelectChange,
  };

  const selectedCount = (form.getFieldValue('selectedSimulations') || [])
    .length;

  return (
    <>
      <Form.Item name="selectedSimulations" hidden initialValue={[]}>
        <input type="hidden" />
      </Form.Item>

      <Flex
        gap={12}
        justify="center"
        align="center"
        style={{ width: '100%', marginBottom: 12 }}
      >
        <SearchInput
          placeholder={t(
            'components.simReport.selectSimulation.searchPlaceholder'
          )}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-group"
          style={{ flex: 5 }}
        />
        <Badge
          count={selectedCount}
          showZero
          style={{
            backgroundColor:
              selectedCount > 0
                ? theme.colors.bluePrimary
                : theme.colors.grayAccent,
            flex: 1,
          }}
        />
        <Typography.Text type="secondary" style={{ flex: 10 }}>
          {t('components.simReport.selectSimulation.note')}
        </Typography.Text>
      </Flex>

      <Table
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={completedSimulationsData?.results}
        loading={completedSimulationsDataIsLoading}
        size="small"
        pagination={{
          total: completedSimulationsData?.count,
          pageSize: pageSize,
          hideOnSinglePage: true,
          defaultCurrent: 1,
          onChange: (page: number) => {
            setOffset(page);
          },
          pageSizeOptions: ['5', '10', '20', '50', '100'],
          onShowSizeChange: (current, size) => {
            setOffset(current);
            setPageSize(size);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: 1080 }}
      />
    </>
  );
};

export default SelectSimulation;
